import { useMemo, useState } from 'react'

import { useShipmentPayments } from '#/entities/shipment'
import type { useCreateToolbarItem } from '#/features/shipment-toolbar'
import { useListPaymentMethodsApiV1PaymentMethodsGet } from '#/shared/api/generated/billing/theCargoBilling'
import { methodToChargeType } from '#/shared/ui/create-payment-modal'
import type { CreatePaymentFormValues } from '#/shared/ui/create-payment-modal'

import { toIsoDate, trimNonEmpty } from './helpers'

const PAYMENT_METHOD_ACTIVE_STATUS = 'active'

function buildPaymentPayload(
  values: CreatePaymentFormValues,
  methodIdByType: Map<string, string>,
) {
  const methodIds = values.methods
    .map((type) => methodIdByType.get(type))
    .filter((id): id is string => Boolean(id))
  return {
    paymentType: values.type,
    direction: values.direction,
    amount: values.amount,
    surcharge: values.surcharge,
    discount: values.discount,
    method: methodToChargeType(values.methods[0]),
    methodIds: methodIds.length > 0 ? methodIds : undefined,
    dueDate: values.date ? toIsoDate(values.date) : undefined,
    memo: trimNonEmpty(values.memo),
  }
}

interface PaymentSliceDeps {
  shipmentId: string | undefined
  armed: boolean
  createPayment: ReturnType<typeof useCreateToolbarItem>['createPayment']
  isPending: boolean
}

export function usePaymentSlice({
  shipmentId,
  armed,
  createPayment,
  isPending,
}: PaymentSliceDeps) {
  const [modalOpen, setModalOpen] = useState(false)
  const {
    payments,
    totals,
    isLoading: isLoadingPayments,
  } = useShipmentPayments(shipmentId, { enabled: armed })
  const { data: methodsData } = useListPaymentMethodsApiV1PaymentMethodsGet(
    { size: 100 },
    { query: { enabled: modalOpen } },
  )
  const methodIdByType = useMemo(() => {
    const map = new Map<string, string>()
    for (const method of methodsData?.data.items ?? []) {
      if (method.status !== PAYMENT_METHOD_ACTIVE_STATUS) continue
      if (!map.has(method.type)) map.set(method.type, method.id)
    }
    return map
  }, [methodsData])
  const handleSubmit = async (values: CreatePaymentFormValues) => {
    if (!shipmentId || isPending) return
    try {
      await createPayment(buildPaymentPayload(values, methodIdByType))
    } catch {
      return // onError already surfaced the failure; keep the modal open
    }
    setModalOpen(false)
  }
  return {
    modalOpen,
    onModalOpenChange: setModalOpen,
    onCreate: () => setModalOpen(true),
    onSubmit: handleSubmit,
    isSubmitting: isPending,
    canSubmit: Boolean(shipmentId),
    items: payments,
    totals,
    isLoading: isLoadingPayments,
  }
}
